import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { filterDataforApi, pageNo, rowCount, setPageNo } from 'utils/redux/reducer/filter-slice';
import { createDropdownData, getListData } from 'utils/validators/HelperFunctions';
import strings from '../../../../common/Translation/Translate';
import axiosInstance from '../../../../utils/axios';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import ListPage from '../../CommonPage';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';

type DropdownOption = { label: string; value: number | string };

const compactFilterSelectSx = {
  height: 36,
  fontSize: 13,
  bgcolor: '#fff',
  '& .MuiSelect-select': { py: 0.75 },
};

const stripFilterKey = (filter: unknown, key: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith(`${key}:`))
    : [];

  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withEqFilter = (filter: unknown, key: string, value: string) => {
  const cleaned = stripFilterKey(filter, key);
  if (!value) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`${key}:eq:${value}`);
  return cleaned;
};

const withRoomFilters = (filter: unknown, roomTypeId: string, mealPlanId: string) =>
  withEqFilter(withEqFilter(filter, 'roomTypeId', roomTypeId), 'mealPlanId', mealPlanId);

const RoomsList: FC = () => {
  const dispatch = useDispatch();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [roomTypes, setRoomTypes] = useState<DropdownOption[]>([]);
  const [mealPlans, setMealPlans] = useState<DropdownOption[]>([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(() => searchParams.get('roomTypeId') || '');
  const [selectedMealPlanId, setSelectedMealPlanId] = useState(() => searchParams.get('mealPlanId') || '');

  useEffect(() => {
    const roomTypeIdFromUrl = searchParams.get('roomTypeId') || '';
    const mealPlanIdFromUrl = searchParams.get('mealPlanId') || '';
    setSelectedRoomTypeId(prev => (prev === roomTypeIdFromUrl ? prev : roomTypeIdFromUrl));
    setSelectedMealPlanId(prev => (prev === mealPlanIdFromUrl ? prev : mealPlanIdFromUrl));
  }, [searchParams]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [{ data: roomTypeData }, { data: mealPlanData }] = await Promise.all([
          axiosInstance.get(URLS.roomTypeDropdown),
          axiosInstance.get(URLS.getMealPlansDropdown),
        ]);
        setRoomTypes(createDropdownData(roomTypeData, ['id', 'name']) || []);
        setMealPlans(createDropdownData(mealPlanData, ['id', 'name']) || []);
      } catch (error) {
        console.error(error);
      }
    };
    void fetchDropdowns();
  }, []);

  const resetToFirstPage = useCallback(() => {
    dispatch(setPageNo(0));
  }, [dispatch]);

  const updateSearchParam = (key: string, value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(key, value);
    else nextParams.delete(key);
    setSearchParams(nextParams, { replace: true });
  };

  const handleRoomTypeFilterChange = (value: string) => {
    setSelectedRoomTypeId(value);
    updateSearchParam('roomTypeId', value);
    resetToFirstPage();
  };

  const handleMealPlanFilterChange = (value: string) => {
    setSelectedMealPlanId(value);
    updateSearchParam('mealPlanId', value);
    resetToFirstPage();
  };

  const pageConfig = useMemo(
    () => ({
      title: 'Rooms',
      entityLabel: 'Rooms',
      subtitle: 'Manage rooms available for booking and pricing.',
      AddButtonText: strings.room_button_add,
      AddButtonUrl: routes.addRooms,
      editButtonUrl: routes.editRooms,
      listApi: URLS.rooms,
      deleteApi: URLS.getRoomsById,
      addPrivilege: privileges.add_rooms,
      editPrivilege: privileges.edit_rooms,
      deletePrivilege: privileges.delete_rooms,
      headerFilters: () => (
        <>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Room type</InputLabel>
            <Select
              label="Room type"
              value={selectedRoomTypeId}
              onChange={event => handleRoomTypeFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All room types</MenuItem>
              {roomTypes.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Meal plan</InputLabel>
            <Select
              label="Meal plan"
              value={selectedMealPlanId}
              onChange={event => handleMealPlanFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All meal plans</MenuItem>
              {mealPlans.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ),
      filterChips: [
        ...(selectedRoomTypeId
          ? [
              {
                key: 'roomTypeId',
                label: `Room type : ${
                  roomTypes.find(option => String(option.value) === selectedRoomTypeId)?.label ||
                  selectedRoomTypeId
                }`,
              },
            ]
          : []),
        ...(selectedMealPlanId
          ? [
              {
                key: 'mealPlanId',
                label: `Meal plan : ${
                  mealPlans.find(option => String(option.value) === selectedMealPlanId)?.label ||
                  selectedMealPlanId
                }`,
              },
            ]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'roomTypeId') handleRoomTypeFilterChange('');
        if (key === 'mealPlanId') handleMealPlanFilterChange('');
      },
      onClearAllFilterChips: () => {
        handleRoomTypeFilterChange('');
        handleMealPlanFilterChange('');
      },
      tableColumn: [
        {
          field: 'roomTypeName',
          headerName: 'Room Name',
          flex: 2,
          sortable: true,
          localField: 'localizedAttributes.name',
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#101828' }}>{value || '—'}</Typography>
          ),
        },
        {
          field: 'mealPlansConfigured',
          headerName: 'Meal Plans',
          flex: 1.2,
          sortable: false,
          renderCell: (value: any) => {
            const count = Number(value ?? 0);
            return (
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: count > 0 ? '#344054' : '#98A2B3' }}>
                {count === 0 ? '0 configured' : count === 1 ? '1 configured' : `${count} configured`}
              </Typography>
            );
          },
        },
        {
          field: 'formattedPriceRange',
          headerName: 'Price',
          flex: 1.4,
          sortable: false,
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#101828' }}>{value || '—'}</Typography>
          ),
        },
        lastUpdatedTableColumn(),
      ],
    }),
    [mealPlans, roomTypes, selectedMealPlanId, selectedRoomTypeId],
  );

  const getList = useCallback(
    async (filterData: any) => {
      const body = filterData
        ? {
            ...filterData,
            filter: withRoomFilters(filterData.filter, selectedRoomTypeId, selectedMealPlanId),
          }
        : {
            page: index + 1,
            size: pageSize,
            filter: withRoomFilters(filter, selectedRoomTypeId, selectedMealPlanId),
            sort: ['createdAt:desc'],
          };
      getListData(setLoading, body, pageConfig.listApi, setPageResponse);
    },
    [pageConfig.listApi, index, pageSize, filter, selectedRoomTypeId, selectedMealPlanId],
  );

  useEffect(() => {
    (async () => {
      await getList(null);
    })();
  }, [getList]);

  return <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />;
};

export default RoomsList;
